// Η περιοχή όπως τη στήνει η Sala (editor κάτοψης), ζωγραφισμένη στον καμβά του ταμείου:
// τοίχοι (ή ανοιχτή αυλή), πόρτες/είσοδοι/παράθυρα πάνω στους τοίχους, μπαρ, κολώνες, φυτά.
//
// Το layout έρχεται σε μονάδες χώρου (50 = 1 μ.), με τα ανοίγματα σε μονάδες από την αρχή
// του τοίχου και τα σταθερά στοιχεία από τη γωνία της περιοχής. Ο καμβάς της ζώνης έχει
// πλάτος 1000 και ύψος από τις αναλογίες της (zoneCanvas)· εδώ γίνονται px του καμβά.
// Ίδια λογική με το xentrix_table src/ui/floor/Venue.tsx (openingParts), ώστε ταμείο και
// Sala να δείχνουν το ίδιο μαγαζί.

export type OpeningType = 'entrance' | 'door' | 'window';
export type Side = 'top' | 'right' | 'bottom' | 'left';
export interface ZoneOpening { id?: string; type: OpeningType; side: Side; at: number; len: number; label?: string | null }
export interface ZoneFixture { id?: string; type: 'bar' | 'column' | 'plant' | 'label'; x: number; y: number; w: number; h: number; label?: string | null }
export interface ZoneLayout { w?: number; h?: number; kind?: 'indoor' | 'outdoor'; openings?: ZoneOpening[]; fixtures?: ZoneFixture[] }

export interface Rect { left: number; top: number; width: number; height: number }
export interface Leaf extends Rect { hinge: 'l' | 'r' | 't' | 'b'; open: 'down' | 'up' | 'right' | 'left' }
export interface OpeningParts {
  key: string;
  type: OpeningType;
  /** Το κομμάτι του τοίχου που λείπει (πόρτα/είσοδος). */
  gap: Rect | null;
  /** Το παράθυρο μέσα στον τοίχο· horiz: σε οριζόντιο τοίχο. */
  window: (Rect & { horiz: boolean }) | null;
  /** Φύλλα πόρτας με την τροχιά τους, μέσα στον χώρο (διπλά στην είσοδο). */
  leaves: Leaf[];
  /** Η ετικέτα (π.χ. «Είσοδος»), μέσα στον χώρο δίπλα στο άνοιγμα. */
  label: { left: number; top: number; text: string; vertical: boolean } | null;
}
export interface RoomParts { indoor: boolean; wall: number; openings: OpeningParts[]; fixtures: (Rect & { key: string; type: ZoneFixture['type']; label: string; vertical: boolean })[] }

/** Πάχος τοίχου σε μονάδες χώρου — όπως στη Sala. */
const WALL = 6;
/** Μέγιστο φύλλο πόρτας (μονάδες): ~90 εκ. */
const LEAF = 46;

const valid = (l?: ZoneLayout | null): l is ZoneLayout & { w: number; h: number } =>
  !!l && Number(l.w) > 0 && Number(l.h) > 0;

/** Υπάρχει σχέδιο χώρου από τη Sala για αυτή τη ζώνη; */
export const hasRoom = (l?: ZoneLayout | null) => valid(l);

export function roomParts(layout: ZoneLayout | null | undefined, canvas: { width: number; height: number }): RoomParts | null {
  if (!valid(layout)) return null;
  const kx = canvas.width / layout.w;
  const ky = canvas.height / layout.h;
  const k = Math.min(kx, ky);
  const indoor = layout.kind !== 'outdoor';
  const wall = indoor ? Math.max(4, WALL * k) : 2;

  const openings = (layout.openings || []).flatMap((o, i): OpeningParts[] => {
    if (!o || !['top', 'right', 'bottom', 'left'].includes(o.side)) return [];
    const horiz = o.side === 'top' || o.side === 'bottom';
    const along = horiz ? kx : ky;
    const span = horiz ? canvas.width : canvas.height;
    const start = Math.max(0, Math.min(span, Number(o.at) * along));
    const len = Math.max(0, Math.min(span - start, Number(o.len) * along));
    if (len <= 0) return [];
    const at = (sideOffset: number): Rect => (horiz
      ? { left: start, top: o.side === 'top' ? 0 : canvas.height - sideOffset, width: len, height: sideOffset }
      : { left: o.side === 'left' ? 0 : canvas.width - sideOffset, top: start, width: sideOffset, height: len });

    const isWindow = o.type === 'window';
    const gap = !isWindow ? at(wall) : null;
    const window = isWindow && indoor ? { ...at(wall), horiz } : null;

    const leaves: Leaf[] = [];
    if (!isWindow && indoor) {
      const double = o.type === 'entrance';
      const s = Math.min(LEAF * k, double ? len / 2 : len);
      if (horiz) {
        const top = o.side === 'top' ? wall : canvas.height - wall - s;
        const open = o.side === 'top' ? 'down' : 'up';
        leaves.push({ left: start, top, width: s, height: s, hinge: 'l', open });
        if (double) leaves.push({ left: start + len - s, top, width: s, height: s, hinge: 'r', open });
      } else {
        const left = o.side === 'left' ? wall : canvas.width - wall - s;
        const open = o.side === 'left' ? 'right' : 'left';
        leaves.push({ left, top: start, width: s, height: s, hinge: 't', open });
        if (double) leaves.push({ left, top: start + len - s, width: s, height: s, hinge: 'b', open });
      }
    }

    // Η ετικέτα μέσα στον χώρο (το ταμείο δεν έχει περιθώριο έξω από τον καμβά).
    const depth = wall + (leaves[0]?.width || 0) + 6;
    const mid = start + len / 2;
    const text = (o.label || '').trim();
    const label = text
      ? horiz
        ? { left: mid, top: o.side === 'top' ? depth : canvas.height - depth - 16, text, vertical: false }
        : { left: o.side === 'left' ? depth : canvas.width - depth - 16, top: mid, text, vertical: true }
      : null;
    return [{ key: o.id || `o${i}`, type: o.type, gap, window, leaves, label }];
  });

  const fixtures = (layout.fixtures || []).flatMap((f, i) => {
    if (!f || !['bar', 'column', 'plant', 'label'].includes(f.type)) return [];
    const r = { left: Number(f.x) * kx, top: Number(f.y) * ky, width: Number(f.w) * kx, height: Number(f.h) * ky };
    if (!(r.width > 0) || !(r.height > 0)) return [];
    return [{ ...r, key: f.id || `f${i}`, type: f.type, label: (f.label || '').trim(), vertical: f.type === 'bar' && r.height > r.width }];
  });

  return { indoor, wall, openings, fixtures };
}
